import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Pencil, Trash2, Search } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast'
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import AsyncSelect from '@/components/crud/AsyncSelect'
import { cn, masks } from '@/lib/utils'

/**
 * Página CRUD genérica para os cadastros auxiliares.
 * Ver configs em src/pages/cadastro/configs.js
 */
export default function CrudPage({ config, backTo, backLabel, subtitle }) {
  const { title, titlePlural, description, endpoint, idKey, columns, fields, searchPlaceholder = 'Buscar...', canDelete = true, modalSize = 'md' } = config
  const toast = useToast()

  const [items, setItems] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const scrollRef = useRef(null)

  const load = useCallback(
    async (reset = false) => {
      if (loading) return
      setLoading(true)
      try {
        const nextOffset = reset ? 0 : offset
        const { data } = await api.get(endpoint, { params: { q, limit: 30, offset: nextOffset } })
        setItems((prev) => (reset ? data.data : [...prev, ...data.data]))
        setOffset(data.offset)
        setHasMore(data.hasMore)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, q, offset, loading]
  )

  // Recarrega ao mudar a busca (com debounce).
  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0)
      load(true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  function onScroll(e) {
    const el = e.target
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      load(false)
    }
  }

  function openNew() {
    const initial = {}
    fields.forEach((f) => {
      initial[f.name] = f.default ?? ''
    })
    setForm(initial)
    setEditing(null)
    setErrors({})
    setModalOpen(true)
  }

  function openEdit(item) {
    const initial = {}
    fields.forEach((f) => {
      initial[f.name] = item[f.name] ?? (f.default ?? '')
    })
    setForm({ ...initial, __raw: item })
    setEditing(item)
    setErrors({})
    setModalOpen(true)
  }

  async function save() {
    setSaving(true)
    setErrors({})
    try {
      const payload = { ...form }
      delete payload.__raw
      if (editing) {
        await api.put(`${endpoint}/${editing[idKey]}`, payload)
      } else {
        await api.post(endpoint, payload)
      }
      setModalOpen(false)
      setOffset(0)
      load(true)
      toast.success(editing ? `${title} atualizado com sucesso.` : `${title} cadastrado com sucesso.`)
    } catch (err) {
      const res = err?.response?.data
      if (res?.errors) setErrors(res.errors)
      else setErrors({ _: res?.error || 'Erro ao salvar.' })
      toast.error('Não foi possível salvar.')
    } finally {
      setSaving(false)
    }
  }

  async function doDelete() {
    if (!confirmDelete) return
    try {
      await api.delete(`${endpoint}/${confirmDelete[idKey]}`)
      setConfirmDelete(null)
      setOffset(0)
      load(true)
      toast.success(`${title} excluído.`)
    } catch (err) {
      setErrors({ _: err?.response?.data?.error || 'Erro ao excluir.' })
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        backTo={backTo}
        backLabel={backLabel}
        title={titlePlural || title}
        subtitle={subtitle || description}
        actions={
          <>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} className="pl-9" />
            </div>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" /> Novo
            </Button>
          </>
        }
      />

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto p-6 pt-4">
        <Table>
          <THead>
            <tr>
              {columns.map((c) => (
                <TH key={c.key} className={c.className}>{c.label}</TH>
              ))}
              <TH className="text-right w-28">Ações</TH>
            </tr>
          </THead>
          <TBody>
            {items.map((item, i) => (
              <TR key={item[idKey]} index={i}>
                {columns.map((c) => (
                  <TD key={c.key} className={c.className}>
                    {c.render ? c.render(item) : item[c.key]}
                  </TD>
                ))}
                <TD className="text-right whitespace-nowrap">
                  <button onClick={() => openEdit(item)} title="Editar" className="p-1.5 rounded-md hover:bg-accent/60 text-muted-foreground hover:text-foreground">
                    <Pencil className="h-4 w-4" />
                  </button>
                  {canDelete && (
                    <button onClick={() => setConfirmDelete(item)} title="Excluir" className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </TD>
              </TR>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <TD colSpan={columns.length + 1} className="text-center text-muted-foreground py-8">
                  Nenhum registro encontrado.
                </TD>
              </tr>
            )}
          </TBody>
        </Table>
        {loading && <p className="text-center text-sm text-muted-foreground py-4">Carregando...</p>}
      </div>

      {/* Modal criar/editar */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar ${title}` : `Novo ${title}`}
        size={modalSize}
        footer={
          <>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <FieldRow key={f.name} field={f} form={form} setForm={setForm} error={errors[f.name]} editing={editing} />
          ))}
          {errors._ && <p className="col-span-2 text-sm text-destructive">{errors._}</p>}
        </div>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Confirmar exclusão"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={doDelete}>Excluir</Button>
          </>
        }
      >
        <p className="text-sm">Tem certeza que deseja excluir este registro?</p>
        {errors._ && <p className="text-sm text-destructive mt-2">{errors._}</p>}
      </Dialog>
    </div>
  )
}

/** Tipos de `field.type` renderizados por um <Input> nativo do mesmo tipo. */
const INPUT_TYPES = ['text', 'number', 'password', 'email']

function FieldRow({ field, form, setForm, error, editing }) {
  const mask = masks[field.mask]
  const raw = form[field.name] ?? ''
  const value = mask ? mask(raw) : raw
  const set = (v) => setForm((prev) => ({ ...prev, [field.name]: mask ? mask(v) : v }))
  const colSpan = field.colSpan === 2 ? 'col-span-2' : 'col-span-2 sm:col-span-1'

  return (
    <div className={cn(colSpan, 'space-y-1.5')}>
      <Label>{field.label}{field.required && <span className="text-destructive"> *</span>}</Label>
      {field.type === 'textarea' && (
        <textarea
          value={value}
          onChange={(e) => set(e.target.value)}
          rows={3}
          className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      )}
      {field.type === 'select' && (
        <Select value={value} onChange={(e) => set(e.target.value)}>
          <option value="">Selecione...</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
      )}
      {field.type === 'asyncSelect' && (
        <AsyncSelect
          endpoint={field.endpoint}
          value={value}
          onChange={set}
          optionValue={field.optionValue}
          optionLabel={field.optionLabel}
          formatLabel={field.formatLabel}
          initialLabel={editing ? (field.initialLabel?.(form.__raw || {}) ?? form.__raw?.[field.labelKey] ?? '') : ''}
        />
      )}
      {(!field.type || INPUT_TYPES.includes(field.type)) && (
        <Input
          type={INPUT_TYPES.includes(field.type) ? field.type : 'text'}
          value={value}
          onChange={(e) => set(e.target.value)}
          placeholder={field.placeholder}
          step={field.step}
        />
      )}
      {field.hint && <p className="text-xs text-muted-foreground">{field.hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}
